import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { WrappedLoading } from '../components/Loading'
import ErrorTryAgain from '../components/ErrorTryAgain'
import {
  fetchBulkOrderCatalog,
  fetchBulkOrderLensDetail,
  selectBulkOrderLens,
  selectCartLineCount,
  selectHasCartError,
  selectIsCartLoading,
} from '../../common/bulkOrder'
import BulkOrderCatalogSidebar from './BulkOrderCatalogSidebar'
import BulkOrderGridPanel from './BulkOrderGridPanel'
import BulkOrderPageShell from './BulkOrderPageShell'
import BulkOrderSteps from './BulkOrderSteps'
import useRetryBulkOrderCartFetch from './useRetryBulkOrderCartFetch'
import {
  BulkOrderBox,
  BulkOrderBoxContent,
  BulkOrderHeader,
  BulkOrderLayout,
  BulkOrderMainCol,
  BulkOrderSidebarCol,
  BulkOrderState,
} from './BulkOrderPage.styles'

const BulkOrderPageContent = () => {
  const dispatch = useDispatch()

  const catalog = useSelector(state => state.bulkOrder.catalog)
  const isCatalogReady = useSelector(state => state.bulkOrder.isCatalogReady)
  const hasCatalogError = useSelector(
    state => state.bulkOrder.hasCatalogError,
  )
  const selectedLensId = useSelector(state => state.bulkOrder.selectedLensId)
  const lensDetail = useSelector(state => state.bulkOrder.lensDetail)
  const cartLineCount = useSelector(selectCartLineCount)
  const isCartLoading = useSelector(selectIsCartLoading)
  const hasCartError = useSelector(selectHasCartError)

  const handleRetryCart = useRetryBulkOrderCartFetch()

  useEffect(() => {
    if (!selectedLensId) return
    if (lensDetail && String(lensDetail._id) === String(selectedLensId)) return
    dispatch(fetchBulkOrderLensDetail(selectedLensId))
  }, [dispatch, selectedLensId, lensDetail])

  const handleSelectLens = useCallback(
    lensId => {
      dispatch(selectBulkOrderLens(lensId))
    },
    [dispatch],
  )

  return (
    <BulkOrderBox>
      <BulkOrderHeader>
        <BulkOrderSteps activeStep={1} cartLineCount={cartLineCount} />
      </BulkOrderHeader>
      <BulkOrderBoxContent>
        {!isCatalogReady && !hasCatalogError && (
          <BulkOrderState>
            <WrappedLoading type="puff" padding="0" />
          </BulkOrderState>
        )}

        {hasCatalogError && !isCatalogReady && (
          <BulkOrderState>
            <ErrorTryAgain
              onClick={() => {
                dispatch(fetchBulkOrderCatalog())
              }}
            />
          </BulkOrderState>
        )}

        {isCatalogReady && (
          <BulkOrderLayout>
            <BulkOrderSidebarCol width="35rem" shrink={0}>
              <BulkOrderCatalogSidebar
                catalog={catalog}
                selectedLensId={selectedLensId}
                onSelectLens={handleSelectLens}
              />
            </BulkOrderSidebarCol>
            <BulkOrderMainCol grow={1} minWidth="0">
              {isCartLoading && <WrappedLoading type="puff" padding="1rem" />}
              {hasCartError && !isCartLoading && (
                <ErrorTryAgain onClick={handleRetryCart} />
              )}
              {!isCartLoading && !hasCartError && <BulkOrderGridPanel />}
            </BulkOrderMainCol>
          </BulkOrderLayout>
        )}
      </BulkOrderBoxContent>
    </BulkOrderBox>
  )
}

const BulkOrderPage = () => {
  const { t } = useTranslation()

  return (
    <BulkOrderPageShell
      metaTitle={t('New bulk order')}
      fillViewport
      compactFooter
    >
      <BulkOrderPageContent />
    </BulkOrderPageShell>
  )
}

export default BulkOrderPage
